import React, { useCallback, useMemo } from "react"
import styled from "styled-components"
import { BodySmall } from "../../atoms/Bodies"
import { TextLink } from "../../atoms/links/TextLink"
import { PlanInfoBadgeContainer } from "../../../containers/planinfo/PlanInfoBadgeContainer"
import { Spacer } from "../../atoms/Atoms"
import { Body, Container, SlidingDotsLoader } from "@carrotfertility/carotene"
import L10n from "lib/carrot-l10n"
import { SwitchH1ToH2 } from "../../fle/Molecules"
import { Modal } from "../../modal/Modal"
import EmployeeOnlyModalContainer from "../../../employee-only-containers/EmployeeOnlyModalContainer"
import { DbgPaths } from "../../../../utils/Paths"
import { MedicationListItems, MedicationsFooter, MedicationsHeader } from "./multiple-categories/MedicationsCategory"
import { OtherExpensesHeader, OtherExpensesListItems } from "./multiple-categories/OtherExpensesCategory"
import { QmeHeader, QmeListItems } from "./multiple-categories/QmeCategory"
import { ModalListItem } from "./ModalListItem"
import useBenefit from "../../../dynamic-content/hooks/useGetBenefit"
import { FinancialSupportErrorMessage } from "./FinancialSupportErrorMessage"
import { getBenefitConfiguration } from "../../../../reducers/benefitConfiguration"
import { useSelector } from "react-redux"
import { getCountryCode } from "../../../../reducers/companyInfo"
import useCompanyComplianceConfigLineItemFilter from "../hooks/useGetCompanyComplianceConfigLineItemFilter"
import DefaultMarkdown from "../../DefaultMarkdown"
import { List } from "@carrotfertility/carotene-core"

const BenefitDisplayPlanTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 600px;
`

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 80%;
  align-self: center;
`

const ModalSectionHeaders = styled.span`
  font-weight: 500;
`

const PlanInfoBadgeMarginWrapper = styled.div`
  margin-left: 1rem;
  margin-bottom: 0.5rem;
`
const l = new L10n({ locale: "en-US" }).getBoundLocalize()

export const PayingForCareContentSub = (props) => {
  const { categoryName, benefitInfo, lineItemFilter } = props
  const isOtherExpenses = useMemo(() => categoryName === "Other expenses", [categoryName])
  const preservation = benefitInfo.journeys.fertilityPreservation
  const fertilityCare = benefitInfo.journeys.fertilityCare
  const hasElectiveFertilityLineItemFilter = lineItemFilter === "ELECTIVE_FERTILITY"
  const showQmeInfertilityMessage =
    fertilityCare.eligibleExpensesDetail.isCoveredWithInfertilityDiagnosis ||
    preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered
  const showOtherExpensesInfertilityMessage =
    fertilityCare.eligibleExpensesDetail.isElectiveCareCovered ||
    preservation.eligibleExpensesDetail.isElectivePreservationCovered

  return (
    <>
      {!hasElectiveFertilityLineItemFilter && (showQmeInfertilityMessage || showOtherExpensesInfertilityMessage) && (
        <div>
          <ModalSectionHeaders>{l`client.reimbursements.library.header.infertilityDiagnosis`}</ModalSectionHeaders>
          <Spacer height={0.5} />
          <Body color="black-80">
            <p>{l`client.reimbursements.library.content.infertilityDiagnosis.intro`}</p>
            <Spacer height={0.5} />
            <p>{l`client.reimbursements.library.content.infertilityDiagnosis.intro2`}</p>
          </Body>
          <Spacer height={0.5} />
          <List>
            <ModalListItem>{l`client.reimbursements.library.infertilityDiagnosisListItem.ovulation`}</ModalListItem>
            <ModalListItem>{l`client.reimbursements.library.infertilityDiagnosisListItem.semen`}</ModalListItem>
            <ModalListItem>
              <DefaultMarkdown
                source={"Anatomical, endocrine, genetic, or functional differences in the reproductive system"}
              />
            </ModalListItem>
          </List>
        </div>
      )}
      <Spacer height={1.0} />
      {!isOtherExpenses &&
        !hasElectiveFertilityLineItemFilter &&
        preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered && (
          <div>
            <ModalSectionHeaders>{l`client.reimbursements.library.header.preservationIsMedicallyNecessary`}</ModalSectionHeaders>
            <Spacer height={0.5} />
            <Body color="black-80">{l`client.reimbursements.library.content.preservationIsMedicallyNecessary`}</Body>
          </div>
        )}
      {isOtherExpenses &&
        !hasElectiveFertilityLineItemFilter &&
        preservation.eligibleExpensesDetail.isElectivePreservationCovered && (
          <div>
            <ModalSectionHeaders>{l`client.reimbursements.library.header.medicallyNecessary`}</ModalSectionHeaders>
            <Spacer height={0.5} />
            <Body color="black-80">{l`client.reimbursements.library.content.medicallyNecessary`}</Body>
          </div>
        )}
    </>
  )
}

const PayingForCareContentMain = (props): JSX.Element => {
  const { categoryName } = props
  const isOtherExpenses = useMemo(() => categoryName === "Other expenses", [categoryName])
  const isMedicationExpenses = useMemo(() => categoryName === "Medication expenses", [categoryName])
  const { data: benefit, isLoading } = useBenefit()
  const { coverageConfig } = useSelector(getBenefitConfiguration)
  const companyGeo = useSelector(getCountryCode)
  const { data } = useCompanyComplianceConfigLineItemFilter()

  if ((data == null || benefit == null) && !isLoading) {
    return <FinancialSupportErrorMessage />
  }

  return isLoading ? (
    <SlidingDotsLoader />
  ) : (
    <ModalContainer>
      <Container padding="none" stack="large">
        <SwitchH1ToH2>{`${categoryName}`}</SwitchH1ToH2>
        <Spacer height={1.0} />
        <div>
          <Body color="black-80">
            {!isOtherExpenses && !isMedicationExpenses && <QmeHeader companyCoverage={coverageConfig} />}
            {!isOtherExpenses && isMedicationExpenses && <MedicationsHeader />}
            {isOtherExpenses && <OtherExpensesHeader />}
          </Body>
          <Spacer height={0.5} />
          <List>
            {!isOtherExpenses && isMedicationExpenses && <MedicationListItems benefitInfo={benefit} />}
            {!isOtherExpenses && !isMedicationExpenses && (
              <QmeListItems benefitInfo={benefit} country={companyGeo} lineItemFilter={data} />
            )}
            {isOtherExpenses && (
              <OtherExpensesListItems
                benefitInfo={benefit}
                country={companyGeo}
                lineItemFilter={data}
                companyCoverage={coverageConfig}
              />
            )}
          </List>
          <Spacer height={0.5} />
          <Body color="black-80">
            {!isOtherExpenses && isMedicationExpenses && <MedicationsFooter benefitInfo={benefit} />}
          </Body>
        </div>
        <Spacer height={1.0} />
        {!isMedicationExpenses && (
          <PayingForCareContentSub categoryName={categoryName} benefitInfo={benefit} lineItemFilter={data} />
        )}
      </Container>
    </ModalContainer>
  )
}

export function PayingForCareCoverageModal({ element, showLinks }): JSX.Element {
  const [showModal, setShowModal] = React.useState(false)
  const handleOpen = useCallback(() => {
    setShowModal(true)
  }, [])
  const handleExit = useCallback(() => {
    setShowModal(false)
  }, [])
  const isAdoption = useMemo(() => element.reimbursementCategoryName.toLowerCase().includes("adoption"), [
    element.reimbursementCategoryName
  ])
  const supportsDeductibles = useMemo(
    () => ["adoption", "gestation", "qualified"].some((name) => element.reimbursementCategoryName.includes(name)),
    [element.reimbursementCategoryName]
  )

  return (
    <BenefitDisplayPlanTextContainer>
      {element.length > 1 && <body className="mr3 ml3 black-80"> {element.reimbursementCategoryName} </body>}
      {!isAdoption && (
        <BodySmall color="mr3 ml3 black-60">
          {showLinks && (
            <p>
              <TextLink onClick={handleOpen}>Learn </TextLink> what this includes
            </p>
          )}
          {showModal && (
            <EmployeeOnlyModalContainer onClose={handleExit}>
              <Modal onExit={handleExit} titleText={element.reimbursementCategoryName}>
                <PayingForCareContentMain categoryName={element.reimbursementCategoryName} />
                <Spacer height={3.0} />
              </Modal>
            </EmployeeOnlyModalContainer>
          )}
          <PlanInfoBadgeMarginWrapper>
            <PlanInfoBadgeContainer
              backgroundColor="light-grey"
              fontColor="black-60"
              subsidy={element}
              categorySupportsDeductibles={supportsDeductibles}
            />
          </PlanInfoBadgeMarginWrapper>
        </BodySmall>
      )}
      {isAdoption && showLinks && (
        <BodySmall color="mr3 ml3 black-60">
          <TextLink to={DbgPaths.ADOPTION_COVERAGE}>Learn</TextLink>
          {" what this includes"}
          <PlanInfoBadgeContainer
            backgroundColor="light-grey"
            fontColor="black-60"
            subsidy={element}
            categorySupportsDeductibles={supportsDeductibles}
          />
        </BodySmall>
      )}
    </BenefitDisplayPlanTextContainer>
  )
}
